import React, { useState } from 'react';
import { CheckCircle2, Download, Trash2, ExternalLink, Facebook, Linkedin, Edit2, MessageSquare, ChevronDown, ChevronUp, AlertCircle, FileText, Search, User, Send, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PeopleTab } from './PeopleTab';
import { FinancialsTab } from './FinancialsTab';
import { DetailsTab } from './DetailsTab';
import { NotificationsTab } from './NotificationsTab';
import { OverviewTab } from './OverviewTab';
import { DunderMifflinApplicationView } from './DunderMifflinApplicationView';
import { NotesModal } from './NotesModal';
import { ScoutAIButton } from './ScoutAIButton';
const dunderMifflinNotes = [{
  id: '1',
  type: 'user' as const,
  author: 'Dave Davidson',
  timestamp: 'Just now',
  title: 'Large Order Expected',
  content: "Spoke with Michael Scott — they're expecting a large Q2 order from a new client. Will revisit limit increase in 6 months if payment history is clean."
}, {
  id: '2',
  type: 'system' as const,
  timestamp: 'March 27, 2026 · 10:17 AM',
  title: 'Credit Approved with Changes',
  content: 'Credit limit set to $35,000 with 45-day terms.'
}, {
  id: '3',
  type: 'system' as const,
  timestamp: 'March 27, 2026 · 10:15 AM',
  title: 'Application Submitted',
  content: 'Submitted by Pam Beesly on behalf of Dunder Mifflin.'
}];
export function CreditApplication() {
  const [activeTab, setActiveTab] = useState('OVERVIEW');
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<null | 'approve' | 'approveWithChanges' | 'reject'>(null);
  const [decisionMade, setDecisionMade] = useState<null | 'approved' | 'approvedWithChanges' | 'rejected'>(null);
  const [expandedRef, setExpandedRef] = useState<string | null>(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const handleDecision = (decision: 'approved' | 'approvedWithChanges' | 'rejected') => {
    setDecisionMade(decision);
    setActiveModal(null);
  };
  return <div className="min-h-full bg-gray-50 p-6" data-id="element-569">
      <div className="max-w-7xl mx-auto space-y-6" data-id="element-570">
        {/* Header Card */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4" data-id="element-571">
          <div className="flex items-center justify-between gap-4" data-id="element-572">
            {/* Left: Company Info + Notes */}
            <div className="shrink-0" data-id="element-573">
              <h1 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-574">
                DUNDER MIFFLIN PAPER COMPANY
              </h1>
              <div className="flex items-center gap-3 text-indigo-600 mb-2" data-id="element-575">
                <ExternalLink className="w-3.5 h-3.5 cursor-pointer" data-id="element-576" />
                <Facebook className="w-3.5 h-3.5 cursor-pointer" data-id="element-577" />
                <Linkedin className="w-3.5 h-3.5 cursor-pointer" data-id="element-578" />
                <Edit2 className="w-3.5 h-3.5 cursor-pointer" data-id="element-579" />
              </div>
              <div className="space-y-0.5 text-xs text-gray-600 mb-2" data-id="element-580">
                <div className="flex items-center gap-1.5" data-id="element-581">
                  <span className="font-semibold text-gray-900" data-id="element-582">
                    Credit Manager:
                  </span>
                  <span data-id="element-583">Sarah Chen</span>
                  <Edit2 className="w-3 h-3 text-indigo-600 cursor-pointer" data-id="element-584" />
                </div>
                <div className="flex items-center gap-1.5" data-id="element-585">
                  <span className="font-semibold text-gray-900" data-id="element-586">Added:</span>
                  <span data-id="element-587">3/27/2026</span>
                </div>
              </div>
              <button onClick={() => setShowNotesModal(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-2.5 py-1 rounded flex items-center gap-1.5 text-xs font-medium relative" data-id="element-588">
                <MessageSquare className="w-3 h-3" data-id="element-589" />
                NOTES
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center" data-id="element-590">
                  {dunderMifflinNotes.length}
                </span>
              </button>
            </div>

            {/* Right: Risk Gauge */}
            <div className="flex flex-col items-center shrink-0 mr-4" data-id="element-591">
              <div className="flex items-end gap-2 mb-1" data-id="element-592">
                <span className="text-xs text-gray-400 font-medium" data-id="element-593">0</span>
                <div className="relative w-64" data-id="element-594">
                  {/* Score label */}
                  <div className="absolute -top-6 left-[82%] -translate-x-1/2 bg-green-100 text-green-800 text-xs font-bold px-2 py-0.5 rounded" data-id="element-595">
                    82
                  </div>
                  {/* Bar */}
                  <div className="relative w-full h-6 flex rounded-full overflow-hidden" data-id="element-596">
                    <div className="w-1/3 bg-red-300" data-id="element-597"></div>
                    <div className="w-1/3 bg-yellow-300" data-id="element-598"></div>
                    <div className="w-1/3 bg-green-300" data-id="element-599"></div>
                  </div>
                  {/* Indicator line */}
                  <div className="absolute top-0 bottom-0 left-[82%] w-0.5 flex flex-col items-center" data-id="element-600">
                    <div className="w-[3px] h-full bg-gray-700 rounded-full" style={{
                    borderLeft: '2px dashed #374151'
                  }} data-id="element-601"></div>
                  </div>
                </div>
                <span className="text-xs text-gray-400 font-medium" data-id="element-602">100</span>
              </div>
              <div className="w-64 flex justify-between px-0 mt-1" data-id="element-603">
                <span className="text-xs text-red-500 font-medium pl-7" data-id="element-604">
                  Low
                </span>
                <span className="text-xs text-yellow-600 font-medium" data-id="element-605">
                  Medium
                </span>
                <span className="text-xs text-gray-600 font-medium pr-7" data-id="element-606">
                  High
                </span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-t border-gray-200 mt-4 pt-3" data-id="element-607">
            {['OVERVIEW', 'NOTIFICATIONS', 'DETAILS', 'FINANCIALS', 'PEOPLE', 'APPLICATIONS'].map(tab => <motion.button key={tab} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={() => {
            setActiveTab(tab);
            setDecisionOpen(false);
          }} className={`flex-1 text-center py-1.5 text-xs font-semibold transition-colors ${tab === activeTab ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-608">
                {tab}
              </motion.button>)}
          </div>
        </div>

        {/* Credit Decision Section */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg shadow-sm overflow-hidden" data-id="element-609">
          {/* Header Row */}
          <button onClick={() => setDecisionOpen(!decisionOpen)} className="w-full p-4 flex items-center justify-between bg-indigo-100 hover:bg-indigo-150 transition-colors" data-id="element-610">
            <div className="flex items-center gap-4" data-id="element-611">
              <span className="font-bold text-gray-900" data-id="element-612">Credit Decision</span>
              <span className="border border-green-500 text-green-600 px-2.5 py-0.5 rounded-full text-sm font-medium" data-id="element-613">
                Ready
              </span>
              <span className="text-sm text-gray-500" data-id="element-614">
                Date: March 27, 2026
              </span>
              <span className="text-sm text-gray-300" data-id="element-615">·</span>
              <span className="text-sm font-medium text-gray-700" data-id="element-616">
                Recommended Credit Limit: $50,000
              </span>
            </div>
            {decisionOpen ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-617" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-618" />}
          </button>

          {/* Expanded Content */}
          {decisionOpen && <div className="p-6 border-t border-indigo-200" data-id="element-619">
              <div className="grid grid-cols-2 gap-x-12 gap-y-6 mb-8" data-id="element-620">
                {/* Left Column */}
                <div className="space-y-4" data-id="element-621">
                  <div data-id="element-622">
                    <p className="text-sm text-gray-500 mb-1" data-id="element-623">
                      Recommended Decision
                    </p>
                    <p className="text-xl font-bold text-gray-900" data-id="element-624">
                      Approve for $50,000
                    </p>
                  </div>
                  <div data-id="element-625">
                    <p className="text-sm font-medium text-gray-700 mb-1" data-id="element-626">
                      Terms
                    </p>
                    <div className="text-sm text-gray-600 space-y-0.5" data-id="element-627">
                      <p data-id="element-628">Payment Terms Days: 30</p>
                      <p data-id="element-629">Discount: 5%</p>
                      <p data-id="element-630">Discount Days: 10</p>
                      <p data-id="element-631">Late Pay Penalty: 1.5%</p>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4" data-id="element-632">
                  <div data-id="element-633">
                    <div className="flex items-center gap-2 mb-1" data-id="element-634">
                      <p className="text-sm text-gray-500" data-id="element-635">Policy Applied</p>
                      <span className="text-sm text-gray-300" data-id="element-636">·</span>
                      <button className="text-sm text-indigo-600 hover:text-indigo-700 italic" data-id="element-637">
                        (View Policy)
                      </button>
                    </div>
                    <p className="font-bold text-gray-900" data-id="element-638">
                      My Company's Credit Policy v1
                    </p>
                  </div>

                  <div data-id="element-639">
                    <div className="flex items-center gap-2 mb-1" data-id="element-640">
                      <p className="text-sm text-gray-500" data-id="element-641">
                        Credit Application
                      </p>
                      <span className="text-sm text-gray-300" data-id="element-642">·</span>
                      <button className="text-sm text-indigo-600 hover:text-indigo-700 italic" data-id="element-643">
                        (View Application)
                      </button>
                    </div>
                    <p className="text-sm text-gray-700" data-id="element-644">
                      Submitted on: March 27, 2026
                    </p>
                  </div>

                  <div data-id="element-645">
                    <div className="flex items-center gap-2 mb-1" data-id="element-646">
                      <p className="text-sm text-gray-500" data-id="element-647">
                        Recommendation Summary
                      </p>
                      <span className="text-sm text-gray-300" data-id="element-648">·</span>
                      <button className="text-sm text-indigo-600 hover:text-indigo-700 italic" data-id="element-649">
                        (Full Reasoning)
                      </button>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed" data-id="element-650">
                      Dunder Mifflin Paper Company, Inc. is approved for credit
                      based on strong business fundamentals, an established
                      77-year operating history, and positive trade references.
                      However, the credit limit is conservatively set due to the
                      applicant's limited initial order size and the need to
                      establish a direct payment history, warranting a measured
                      initial credit exposure.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-4 gap-4" data-id="element-651">
                <button onClick={() => setActiveModal('approve')} className="bg-green-700 hover:bg-green-800 text-white py-3 rounded-lg font-semibold text-sm" data-id="element-652">
                  APPROVE
                </button>
                <button onClick={() => setActiveModal('approveWithChanges')} className="bg-yellow-600 hover:bg-yellow-700 text-white py-3 rounded-lg font-semibold text-sm" data-id="element-653">
                  APPROVE WITH CHANGES
                </button>
                <button onClick={() => setActiveModal('reject')} className="bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-semibold text-sm" data-id="element-654">
                  REJECT
                </button>
                <button className="border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50 py-3 rounded-lg font-semibold text-sm" data-id="element-655">
                  REGENERATE RECOMMENDATION
                </button>
              </div>
            </div>}
        </div>

        {/* Decision Modals */}
        <AnimatePresence data-id="element-656">
          {activeModal && <>
              {/* Backdrop */}
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onClick={() => setActiveModal(null)} className="fixed inset-0 bg-black/50 z-50" data-id="element-657" />

              {/* Modal */}
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-658">
                <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6" data-id="element-659">
                  {activeModal === 'approve' && <>
                      <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-660">
                        Approve Recommendation
                      </h2>
                      <div className="space-y-4" data-id="element-661">
                        <div className="grid grid-cols-3 gap-3" data-id="element-662">
                          <fieldset className="border border-gray-300 rounded px-2 pt-1 pb-2 col-span-2" data-id="element-663">
                            <legend className="text-xs text-gray-500 px-1" data-id="element-664">
                              Credit Limit
                            </legend>
                            <input type="text" defaultValue="50,000" className="w-full text-sm text-gray-900 outline-none" data-id="element-665" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-2 pt-1 pb-2" data-id="element-666">
                            <legend className="text-xs text-gray-500 px-1" data-id="element-667">
                              Currency
                            </legend>
                            <select className="w-full text-sm text-gray-900 outline-none bg-transparent" data-id="element-668">
                              <option data-id="element-669">USD</option>
                            </select>
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-670">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-671">
                            Payment Terms Days
                          </legend>
                          <input type="text" defaultValue="30" className="w-full text-lg text-gray-900 outline-none" data-id="element-672" />
                        </fieldset>
                        <div className="grid grid-cols-2 gap-3" data-id="element-673">
                          <fieldset className="border border-gray-300 rounded px-2 pt-1 pb-2" data-id="element-674">
                            <legend className="text-xs text-gray-500 px-1" data-id="element-675">
                              Discount (%)
                            </legend>
                            <input type="text" placeholder="" className="w-full text-sm text-gray-900 outline-none" data-id="element-676" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-2 pt-1 pb-2" data-id="element-677">
                            <legend className="text-xs text-gray-500 px-1" data-id="element-678">
                              Discount Days
                            </legend>
                            <input type="text" placeholder="" className="w-full text-sm text-gray-900 outline-none" data-id="element-679" />
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-2 pt-1 pb-2" data-id="element-680">
                          <legend className="text-xs text-gray-500 px-1" data-id="element-681">
                            Late Pay Penalty (%)
                          </legend>
                          <input type="text" placeholder="" className="w-full text-sm text-gray-900 outline-none" data-id="element-682" />
                        </fieldset>
                        <fieldset className="border border-gray-300 rounded px-2 pt-1 pb-2" data-id="element-683">
                          <legend className="text-xs text-gray-500 px-1" data-id="element-684">
                            Reasoning
                          </legend>
                          <textarea rows={3} defaultValue="Approved based on strong financials and positive trade references." className="w-full text-sm text-gray-900 outline-none resize-none" data-id="element-685" />
                        </fieldset>
                      </div>
                      <div className="flex justify-end gap-3 mt-6" data-id="element-686">
                        <button onClick={() => setActiveModal(null)} className="text-indigo-600 font-semibold px-4 py-2 text-sm hover:bg-indigo-50 rounded" data-id="element-687">
                          CANCEL
                        </button>
                        <button onClick={() => handleDecision('approved')} className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-6 py-2 text-sm rounded" data-id="element-688">
                          CONFIRM APPROVAL
                        </button>
                      </div>
                    </>}

                  {activeModal === 'approveWithChanges' && <>
                      <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-689">
                        Approve and Input Values
                      </h2>
                      <div className="space-y-6" data-id="element-690">
                        <div className="grid grid-cols-3 gap-4" data-id="element-691">
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3 col-span-2" data-id="element-692">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-693">
                              Credit Limit *
                            </legend>
                            <input type="text" defaultValue="50,000" className="w-full text-lg text-gray-900 outline-none" data-id="element-694" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-695">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-696">
                              Currency
                            </legend>
                            <select className="w-full text-lg text-gray-900 outline-none bg-transparent" data-id="element-697">
                              <option data-id="element-698">USD</option>
                            </select>
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-699">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-700">
                            Payment Terms Days
                          </legend>
                          <input type="text" defaultValue="30" className="w-full text-lg text-gray-900 outline-none" data-id="element-701" />
                        </fieldset>
                        <div className="grid grid-cols-2 gap-4" data-id="element-702">
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-703">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-704">
                              Discount (%)
                            </legend>
                            <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-705" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-706">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-707">
                              Discount Days
                            </legend>
                            <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-708" />
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-709">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-710">
                            Late Pay Penalty (%)
                          </legend>
                          <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-711" />
                        </fieldset>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-712">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-713">
                            Reasoning
                          </legend>
                          <textarea rows={4} className="w-full text-lg text-gray-900 outline-none resize-none" data-id="element-714" />
                        </fieldset>
                      </div>
                      <div className="flex justify-end gap-4 mt-8" data-id="element-715">
                        <button onClick={() => setActiveModal(null)} className="text-indigo-600 font-semibold px-6 py-3 hover:bg-indigo-50 rounded" data-id="element-716">
                          CANCEL
                        </button>
                        <button onClick={() => handleDecision('approvedWithChanges')} className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-3 rounded" data-id="element-717">
                          CONFIRM APPROVAL
                        </button>
                      </div>
                    </>}

                  {activeModal === 'reject' && <>
                      <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-718">
                        Reject
                      </h2>
                      <div className="space-y-6" data-id="element-719">
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-720">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-721">
                            Reasoning
                          </legend>
                          <textarea rows={6} className="w-full text-lg text-gray-900 outline-none resize-none" data-id="element-722" />
                        </fieldset>
                      </div>
                      <div className="flex justify-end gap-4 mt-8" data-id="element-723">
                        <button onClick={() => setActiveModal(null)} className="text-indigo-600 font-semibold px-6 py-3 hover:bg-indigo-50 rounded" data-id="element-724">
                          CANCEL
                        </button>
                        <button onClick={() => handleDecision('rejected')} className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-3 rounded" data-id="element-725">
                          CONFIRM REJECTION
                        </button>
                      </div>
                    </>}
                </div>
              </motion.div>
            </>}
        </AnimatePresence>

        {activeTab === 'OVERVIEW' ? <OverviewTab data-id="element-726" /> : activeTab === 'FINANCIALS' ? <FinancialsTab data-id="element-727" /> : activeTab === 'PEOPLE' ? <PeopleTab data-id="element-728" /> : activeTab === 'DETAILS' ? <DetailsTab data-id="element-729" /> : activeTab === 'NOTIFICATIONS' ? <NotificationsTab data-id="element-730" /> : activeTab === 'APPLICATIONS' ? <DunderMifflinApplicationView data-id="element-731" /> : <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center text-gray-500" data-id="element-732">
            Content for {activeTab} tab coming soon.
          </div>}
      </div>

      <NotesModal isOpen={showNotesModal} onClose={() => setShowNotesModal(false)} notes={dunderMifflinNotes} companyName="DUNDER MIFFLIN PAPER COMPANY" data-id="element-733" />

      <ScoutAIButton companyName="dunder" data-id="element-734" />
    </div>;
}